
import React from 'react';
import { Button } from '@/components/ui/button';
import { Story } from '@/utils/storiesData';
import StoryCard from './StoryCard';
import { useIsMobile } from '@/hooks/use-mobile';
import { useTheme } from '@/contexts/ThemeContext';
import { cn } from '@/lib/utils';
import HorizontalScroller from '@/components/mobile/HorizontalScroller';

interface StoriesGridProps {
  stories: Story[];
  filter: string;
  onClearFilters: () => void;
  searchQuery?: string;
}

const StoriesGrid: React.FC<StoriesGridProps> = ({ 
  stories, 
  filter, 
  onClearFilters,
  searchQuery = '' 
}) => {
  const isMobile = useIsMobile();
  const { resolvedTheme } = useTheme();
  const isDark = resolvedTheme === 'dark';
  const textColor = isDark ? 'text-white' : 'text-black';

  // Determine the title based on filter and search
  const getTitle = () => {
    if (searchQuery) {
      return `Search Results for "${searchQuery}"`;
    }
    return filter === 'all' ? 'All Stories' : `${filter.charAt(0).toUpperCase() + filter.slice(1)} Stories`;
  };

  return (
    <div id="stories-grid">
      <h2 className={cn("text-2xl font-normal mb-8 flex items-center font-gt-super", textColor)}>
        <span className="w-8 h-[2px] bg-primary mr-3"></span>
        {getTitle()}
      </h2>
      
      {stories.length > 0 ? (
        isMobile ? (
          <HorizontalScroller showNextCard={true}>
            {stories.map(story => (
              <StoryCard 
                key={story.id} 
                story={story}
              />
            ))}
          </HorizontalScroller>
        ) : (
          <div className="max-w-screen-2xl mx-auto">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 2xl:grid-cols-4 gap-6 lg:gap-8">
              {stories.map((story) => (
                <StoryCard 
                  key={story.id} 
                  story={story} 
                  featured={false}
                />
              ))}
            </div>
          </div>
        )
      ) : (
        <div className="text-center py-20">
          <p className={cn("text-lg", textColor)}>
            {searchQuery 
              ? `No stories found matching "${searchQuery}".` 
              : 'No stories found matching your criteria.'}
          </p>
          <Button 
            variant="outline" 
            className="mt-4"
            onClick={onClearFilters}
          >
            Clear filters
          </Button>
        </div>
      )}
    </div>
  );
};

export default StoriesGrid;
